import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import DefaultButton from "../../../../components/Buttons/Default";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import AgGridTable from "../../../../components/Tables/AgGrid";
import DefaultPagination from "../../../../components/Pagination";
import sidebar from "../../utils/sidebar";
import { get } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { formatMoney, printPayrollStatus } from "../../utils/payrollStatus";

function PayrollPage() {
  const { t } = useTranslation();
  const { formatDate, settings } = useSettings();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const currency = settings?.sales?.base_currency || '';

  const getPayrollRuns = async () => {
    setLoading(true);
    const statusQuery = statusFilter ? `&status=${statusFilter}` : '';
    const data = await get(`/hr/payroll-runs?page=${currentPage}&page_size=${pageSize}${statusQuery}`);
    if (data?.success) {
      setRowData(data.payroll_runs.data);
      setTotalPages(data.payroll_runs.last_page);
      setTotalRecords(data.payroll_runs.total);
    }
    setLoading(false);
  };

  useEffect(() => {
    getPayrollRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, statusFilter]);

  const columnDefs = useMemo(() => [
    { field: 'reference', headerName: t('hr.payrollRun.reference'), width: 150, sortable: true },
    {
      field: 'name',
      headerName: t('common.name'),
      flex: 1,
      sortable: true,
      valueFormatter: (p) => p.value || '—'
    },
    {
      field: 'period_start',
      headerName: t('hr.payrollRun.period'),
      width: 220,
      valueFormatter: (p) => `${formatDate(p.data.period_start)} → ${formatDate(p.data.period_end)}`
    },
    { field: 'employees_count', headerName: t('hr.payrollRun.employees'), width: 120, sortable: true },
    {
      field: 'total_gross',
      headerName: t('hr.benefits.grossSalary'),
      width: 150,
      sortable: true,
      valueFormatter: (p) => formatMoney(p.value, currency)
    },
    {
      field: 'total_net',
      headerName: t('hr.benefits.netSalary'),
      width: 150,
      sortable: true,
      valueFormatter: (p) => formatMoney(p.value, currency)
    },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 130,
      sortable: true,
      cellRenderer: (p) => printPayrollStatus(p.value)
    },
    {
      field: 'payment_date',
      headerName: t('hr.payrollRun.paymentDate'),
      width: 140,
      valueFormatter: (p) => (p.value ? formatDate(p.value) : '—')
    }
  ], [t, formatDate, currency]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    navigate(`/hr/payroll/view/${event.data.id}`);
  };

  const STATUS_TABS = [
    { value: '', label: t('common.all') },
    { value: 'draft', label: t('hr.payrollRun.status.draft') },
    { value: 'approved', label: t('hr.payrollRun.status.approved') },
    { value: 'paid', label: t('hr.payrollRun.status.paid') },
    { value: 'cancelled', label: t('hr.payrollRun.status.cancelled') }
  ];

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('hr.payroll')} onReload={getPayrollRuns} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/hr/payroll/create')}>
            {t('hr.payrollRun.newRun')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <div className="d-flex gap-2 mb-3 flex-wrap">
            {STATUS_TABS.map(tab => (
              <DefaultButton
                key={tab.value}
                size="sm"
                color={statusFilter === tab.value ? 'primary' : 'secondary'}
                outline={statusFilter !== tab.value}
                onClick={() => {
                  setStatusFilter(tab.value);
                  setCurrentPage(1);
                }}
              >
                {tab.label}
              </DefaultButton>
            ))}
          </div>

          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </SidebarLayout>
  );
}

export default PayrollPage;
